/**
 * School Request Validation Schemas
 *
 * Zod validation schemas for school registration request forms.
 * Uses OpenAPI-generated types to ensure schema matches backend structure.
 *
 * Pattern: Zod schema → react-hook-form → Direct API submission (no mapping!)
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { z } from 'zod';
import type { SchoolRequestDto } from '@/api/generated/types.gen';
import {
  addressSchema,
  phoneSchema,
  cuiSchema,
  requiredText,
  requiredBoolean,
} from '@/shared/validation/common.schemas';
import {
  saptamanaVerdeCampuri,
  ambeleSauNiciuna,
  startNuDupaSfarsit,
  intervalCompletatSauNecunoscut,
  MESAJ_AMBELE_SAU_NICIUNA,
  MESAJ_START_NU_DUPA_SFARSIT,
  MESAJ_INTERVAL_OBLIGATORIU,
} from '@/shared/validation/saptamana-verde';

/** Nicio optiune de livrare aleasa (nici «la adresa scolii», nici «la alta adresa»). */
export const MESAJ_LIVRARE_NEALEASA = 'Alegeți unde se face livrarea';

/**
 * School Request Form Schema
 *
 * Validates all fields required for school registration request.
 * Field names match backend API (SchoolRequestDto) exactly - NO mapping needed!
 *
 * Key Features:
 * - Uses backend field names (adresaLivrareAceeasiCuScoala, prinPrezentaConfirmamInscrierea, etc.)
 * - Nested address validation via addressSchema
 * - 8 required agreement checkboxes
 * - Conditional delivery address validation
 * - Direct API submission (form data → API without transformation)
 *
 * Usage:
 * ```ts
 * const form = useForm<SchoolRequestFormData>({
 *   resolver: zodResolver(schoolRequestSchema),
 * });
 * ```
 */
export const schoolRequestSchema = z
  .object({
    // ===========================================================================
    // IDENTIFIERS (Optional, managed by backend)
    // ===========================================================================
    id: z.string().optional(),
    professorId: z.string().optional(),
    editionId: z.string().optional(),

    // ===========================================================================
    // SCHOOL INFORMATION (Required)
    // ===========================================================================
    numeInstitutieInvatamant: requiredText('Numele instituției este obligatoriu'),
    codSiiirUnitate: requiredText('Codul SIIIR este obligatoriu'),
    cui: cuiSchema,
    codSe: z.string().optional(),
    codSeNumeric: z.number().optional(),

    // ===========================================================================
    // CONTACT & REPRESENTATIVE (Required)
    // ===========================================================================
    numeReprezentatLegal: requiredText('Numele reprezentantului legal este obligatoriu'),
    telefon: phoneSchema,
    // SE-29: `z.coerce.number()` pe `undefined` (camp gol) da `NaN`, care pica pe
    // `invalid_type_error`, NU pe `.min(1)` -- deci un profesor care lasa campul
    // necompletat (cazul real, cel mai comun) vedea „trebuie sa fie un numar", mesajul
    // scris pentru cineva care a tastat text, desi `<input type="number">` deja
    // blocheaza asta din browser. Acelasi mesaj ca `.min(1)`: din perspectiva
    // utilizatorului e acelasi caz.
    numarElevi: z.coerce
      .number({ invalid_type_error: 'Numărul de elevi este obligatoriu' })
      .min(1, 'Numărul de elevi este obligatoriu')
      .int('Numărul de elevi trebuie să fie un număr întreg'),

    // ===========================================================================
    // SCHOOL ADDRESS (Required)
    // ===========================================================================
    adresa: addressSchema,

    // ===========================================================================
    // DELIVERY ADDRESS (Conditional)
    // ===========================================================================
    // ALEGERE EXPLICITA, fara `.default(true)` (decizie owner, 18.09): o bifa pre-bifata inseamna ca
    // „nu m-am atins de ea" se citeste „livrati la adresa scolii", iar profesorul care n-a vazut
    // intrebarea primeste coletul la o adresa pe care n-a confirmat-o. Acum lipsa unei alegeri e o
    // EROARE, pe camp, nu o valoare implicita. Tipul ramane `boolean` (contractul cu backendul).
    adresaLivrareAceeasiCuScoala: z.boolean({
      required_error: MESAJ_LIVRARE_NEALEASA,
      invalid_type_error: MESAJ_LIVRARE_NEALEASA,
    }),
    adresaLivrare: addressSchema.optional(),

    // ===========================================================================
    // CONTAINER INFORMATION
    // ===========================================================================
    detineRecipiente: z.boolean().default(false),

    // ===========================================================================
    // SAPTAMANA VERDE (T12, p8) -- optionala (Ruling S0, 2026-09-11; owner 2026-09-15).
    // Campurile si regulile sunt comune cu `schoolDetailsSchema`: shared/validation/saptamana-verde.ts.
    // ===========================================================================
    ...saptamanaVerdeCampuri,

    // ===========================================================================
    // AGREEMENTS (8 Required Checkboxes)
    // Field names match backend EXACTLY!
    // ===========================================================================
    prinPrezentaConfirmamInscrierea: requiredBoolean(
      'Trebuie să confirmați înscrierea în program'
    ),
    amFostInformati: requiredBoolean('Trebuie să confirmați că ați fost informați'),
    confirmFaptulCaReprezentatulLegal: requiredBoolean(
      'Trebuie să confirmați că reprezentantul legal este de acord'
    ),
    prinPrezentaConfirmCaAmCititInformarea: requiredBoolean(
      'Trebuie să confirmați că ați citit informarea'
    ),
    confirmCaInCazulInCareProiectul: requiredBoolean(
      'Trebuie să confirmați condițiile proiectului'
    ),
    consimtPentruPastrareaDatelorPersonale: requiredBoolean(
      'Trebuie să acordați consimțământul pentru prelucrarea datelor personale'
    ),
    confirmCaAmAcordulDePrincipiu: requiredBoolean(
      'Trebuie să confirmați acordul de principiu'
    ),
    amCititSiSuntDeAcord: requiredBoolean(
      'Trebuie să confirmați că ați citit și sunteți de acord cu termenii'
    ),

    // Optional agreement (not required)
    ecoticPoateFolosiImagini: z.boolean().optional(),
    // Acord optional pentru grupul de WhatsApp „Școala Ecoterrienilor” (data o pune backendul)
    acordGrupWhatsapp: z.boolean().optional(),

    // ===========================================================================
    // ADMIN FIELDS (Managed by backend, included for completeness)
    // ===========================================================================
    approvalStatus: z.enum(['PENDING', 'APPROVED', 'REJECTED']).optional(),
    adminRejectReason: z.string().optional(),
    timestamp: z.date().optional(),
    timestampRange: z
      .object({
        start: z.date().optional(),
        end: z.date().optional(),
      })
      .optional(),
    companyId: z.string().optional(),
  })
  .refine(
    (data) => {
      // If delivery address is different from school address,
      // then delivery address must be provided
      if (!data.adresaLivrareAceeasiCuScoala) {
        return !!data.adresaLivrare;
      }
      return true;
    },
    {
      // Frunza, nu obiectul (SE-07): `adresaLivrare` in sine nu are niciun input
      // randat (campurile sunt `adresaLivrare.judet`, `.oras` etc.), deci o eroare pe
      // calea obiect nu marcheaza niciun `aria-invalid` in DOM — `focusFirstInvalidField()`
      // gasea 0 campuri si toastul cadea pe mesajul generic de rezerva.
      message: 'Adresa de livrare este obligatorie când este diferită de adresa școlii',
      path: ['adresaLivrare', 'judet'],
    }
  )
  .refine(ambeleSauNiciuna, MESAJ_AMBELE_SAU_NICIUNA)
  .refine(startNuDupaSfarsit, MESAJ_START_NU_DUPA_SFARSIT)
  .refine(intervalCompletatSauNecunoscut, MESAJ_INTERVAL_OBLIGATORIU) satisfies z.ZodType<Partial<Omit<SchoolRequestDto, 'saptamanaVerdeStart' | 'saptamanaVerdeEnd'>>>;

/**
 * TypeScript Type for School Request Form Data
 *
 * Inferred from Zod schema. Use this type for:
 * - Form hook return types
 * - Mutation function parameters
 * - Component props
 *
 * Example:
 * ```ts
 * const handleSubmit = async (data: SchoolRequestFormData) => {
 *   await schoolRequestsApi.create(data); // Direct submission!
 * };
 * ```
 */
export type SchoolRequestFormData = z.output<typeof schoolRequestSchema>;

/**
 * Valorile LIVE ale formularului, inainte de validare.
 *
 * Difera de SchoolRequestFormData pentru ca schema are `.default()` pe
 * detineRecipiente: la intrare e optional, la iesire e garantat. (`adresaLivrareAceeasiCuScoala`
 * NU mai are default din 18.09 — trebuie aleasa explicit.) `TFieldValues` din react-hook-form descrie intrarea, nu
 * iesirea — de aici veneau cele 32 de erori de resolver.
 */
export type SchoolRequestFormInput = z.input<typeof schoolRequestSchema>;
